<h2 mat-dialog-title class="dialog-title-block">
  <span class="dialog-title">Proformafaktura</span>
  @if (proforma) {
  <span class="dialog-subtitle">{{ proforma.event_name }}</span>
  }
</h2>

<mat-dialog-content>
  @if (loading) {
  <p class="loading-text">Henter proformafaktura…</p>
  } @else if (proforma) {
  <section class="meta-grid">
    <div>
      <h3 class="subsection-title">Kunde</h3>
      <p>
        <strong>{{ proforma.customer.name }}</strong>
      </p>
      @if (proforma.customer.address) {
      <p>{{ proforma.customer.address }}</p>
      } @if (proforma.customer.ean) {
      <p>EAN: {{ proforma.customer.ean }}</p>
      } @if (proforma.customer.cvr) {
      <p>CVR: {{ proforma.customer.cvr }}</p>
      }
    </div>
    <div>
      <h3 class="subsection-title">Event</h3>
      <p>
        <strong>{{ proforma.event_name }}</strong>
      </p>
      <p>
        {{ proforma.start_date | date : "dd/MM/yyyy" }} –
        {{ proforma.end_date | date : "dd/MM/yyyy" }}
      </p>
      @if (proforma.organization_name) {
      <p>{{ proforma.organization_name }}</p>
      }
    </div>
  </section>

  <mat-divider></mat-divider>

  <section class="form-section">
    <div class="form-grid">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Pristype</mat-label>
        <mat-select
          [(ngModel)]="form.revenue_model"
          panelClass="proforma-select-panel"
        >
          <mat-option value="per_participant">Per deltager</mat-option>
          <mat-option value="fixed_course">Fastsat beløb</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>{{
          form.revenue_model === "per_participant"
            ? "Pris pr. deltager (kr.)"
            : "Samlet pris (kr.)"
        }}</mat-label>
        <input
          matInput
          type="number"
          [(ngModel)]="form.price"
          min="0"
          step="0.01"
        />
      </mat-form-field>
    </div>

    @if (form.revenue_model === 'per_participant') {
    <div class="participant-summary">
      <p>
        Tilmeldte: <strong>{{ registeredCount }}</strong>
      </p>
      <p>
        Gennemførte: <strong>{{ completedCount }}</strong>
      </p>
      <p>
        Pris × tilmeldte:
        <strong>{{ formatKr(form.price * registeredCount) }} kr.</strong>
      </p>
      <p>
        Pris × gennemførte:
        <strong>{{ formatKr(form.price * completedCount) }} kr.</strong>
      </p>
      <p>
        Faktureringsgrundlag: <strong>{{ billingCount }}</strong> →
        <strong>{{ formatKr(baseAmount) }} kr.</strong>
      </p>
      <p class="lines-hint">
        Kunden faktureres for alle tilmeldte (ekskl. instruktør), ikke kun
        gennemførte.
      </p>
    </div>

    <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
      <mat-label>Faktureringsgrundlag</mat-label>
      <mat-select
        [(ngModel)]="form.billing_participant_basis"
        (selectionChange)="onBasisChange()"
        panelClass="proforma-select-panel"
      >
        <mat-option value="registered">Alle tilmeldte</mat-option>
        <mat-option value="completed">Kun gennemførte</mat-option>
        <mat-option value="override">Manuelt antal</mat-option>
      </mat-select>
    </mat-form-field>

    @if (form.billing_participant_basis === 'override') {
    <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
      <mat-label>Antal deltagere (fakturering)</mat-label>
      <input
        matInput
        type="number"
        [(ngModel)]="form.billing_participant_count_override"
        min="0"
        step="1"
      />
    </mat-form-field>
    } @if (proforma.registered_participants?.length) {
    <div class="participant-list">
      <h4 class="subsection-title">Tilmeldte deltagere (ekskl. instruktør)</h4>
      <ul>
        @for (p of proforma.registered_participants; track p) {
        <li>
          {{ p.name }}
          <span class="status-badge">{{
            participantStatusLabel(p.status)
          }}</span>
        </li>
        }
      </ul>
    </div>
    } }
  </section>

  @if (priceExtrasLines.length) {
  <section class="lines-section">
    <h3 class="section-title">Pris-ekstra</h3>
    <table mat-table [dataSource]="priceExtrasLines" class="lines-table">
      <ng-container matColumnDef="label">
        <th mat-header-cell *matHeaderCellDef>Beskrivelse</th>
        <td mat-cell *matCellDef="let line">{{ line.label }}</td>
      </ng-container>
      <ng-container matColumnDef="quantity">
        <th mat-header-cell *matHeaderCellDef>Antal</th>
        <td mat-cell *matCellDef="let line">
          <input
            class="inline-number"
            type="number"
            [(ngModel)]="line.quantity"
            min="0"
            step="1"
          />
        </td>
      </ng-container>
      <ng-container matColumnDef="unit_price">
        <th mat-header-cell *matHeaderCellDef>Enhedspris</th>
        <td mat-cell *matCellDef="let line">
          <input
            class="inline-number"
            type="number"
            [(ngModel)]="line.unit_price"
            min="0"
            step="0.01"
          />
        </td>
      </ng-container>
      <ng-container matColumnDef="line_total">
        <th mat-header-cell *matHeaderCellDef>Total</th>
        <td mat-cell *matCellDef="let line">
          {{ formatKr(lineTotal(line)) }} kr.
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="lineColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: lineColumns"></tr>
    </table>
  </section>
  } @if (cateringExtrasLines.length) {
  <section class="lines-section">
    <h3 class="section-title">Forplejnings-ekstra</h3>
    <p class="lines-hint">
      Priser hentes fra kataloget. Antal er sat til tilmeldte deltagere (ekskl.
      instruktør), så der faktureres for alle der var tilmeldt — ikke kun dem
      der gennemførte.
    </p>
    <table mat-table [dataSource]="cateringExtrasLines" class="lines-table">
      <ng-container matColumnDef="label">
        <th mat-header-cell *matHeaderCellDef>Beskrivelse</th>
        <td mat-cell *matCellDef="let line">
          {{ line.label }}
          @if (line.comment) {
          <span class="line-comment">({{ line.comment }})</span>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="quantity">
        <th mat-header-cell *matHeaderCellDef>Antal</th>
        <td mat-cell *matCellDef="let line">
          <input
            class="inline-number"
            type="number"
            [(ngModel)]="line.quantity"
            min="0"
            step="1"
          />
        </td>
      </ng-container>
      <ng-container matColumnDef="unit_price">
        <th mat-header-cell *matHeaderCellDef>Enhedspris</th>
        <td mat-cell *matCellDef="let line">
          <input
            class="inline-number"
            type="number"
            [(ngModel)]="line.unit_price"
            min="0"
            step="0.01"
          />
        </td>
      </ng-container>
      <ng-container matColumnDef="line_total">
        <th mat-header-cell *matHeaderCellDef>Total</th>
        <td mat-cell *matCellDef="let line">
          {{ formatKr(lineTotal(line)) }} kr.
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="lineColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: lineColumns"></tr>
    </table>
  </section>
  }

  <section class="legacy-extra">
    <mat-checkbox [(ngModel)]="form.has_extra" color="primary">
      Tilføj manuelt ekstra beløb
    </mat-checkbox>
    @if (form.has_extra) {
    <div class="form-grid">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Ekstra beløb (kr.)</mat-label>
        <input
          matInput
          type="number"
          [(ngModel)]="form.extra_amount"
          min="0"
          step="0.01"
        />
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Kommentar</mat-label>
        <textarea
          matInput
          [(ngModel)]="form.extra_amount_comment"
          rows="2"
        ></textarea>
      </mat-form-field>
    </div>
    }
  </section>

  <section class="total-section">
    <div class="total-row">
      <span>Grundbeløb</span><strong>{{ formatKr(baseAmount) }} kr.</strong>
    </div>
    @if (extrasTotal > 0) {
    <div class="total-row">
      <span>Extras</span><strong>{{ formatKr(extrasTotal) }} kr.</strong>
    </div>
    } @if (legacyExtraAmount > 0) {
    <div class="total-row">
      <span>Manuelt ekstra</span
      ><strong>{{ formatKr(legacyExtraAmount) }} kr.</strong>
    </div>
    }
    <div class="total-row total-row--grand">
      <span>Total</span><strong>{{ formatKr(computedTotal) }} kr.</strong>
    </div>
  </section>

  @if (error) {
  <p class="error-text">{{ error }}</p>
  } }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button (click)="cancel()" [disabled]="saving || loading">
    Annuller
  </button>
  <button
    mat-raised-button
    color="primary"
    (click)="save()"
    [disabled]="saving || loading"
  >
    {{ saving ? "Gemmer..." : "Gem proformafaktura" }}
  </button>
</mat-dialog-actions>
